import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowRight } from 'react-icons/fi';
import Container from '../../../components/ui/Container';
import OptimizedImage from '../../../components/ui/OptimizedImage';
import TextReveal from '../../../components/ui/TextReveal';
import MagneticButton from '../../../components/ui/MagneticButton';
import TiltCard from '../../../components/ui/TiltCard';
import ParallaxLayer from '../../../components/ui/ParallaxLayer';
import ScrollReveal from '../../../components/ui/ScrollReveal';

const checkVariants = {
  hidden: { pathLength: 0, opacity: 0 },
  visible: (i) => ({
    pathLength: 1,
    opacity: 1,
    transition: { duration: 0.5, delay: 0.3 + i * 0.15, ease: 'easeOut' },
  }),
};

const features = ['No Equipment Needed', 'Train Anywhere', 'Build Real Strength', 'All Fitness Levels'];

const WhyCalisthenicsSection = ({ data }) => {
  if (!data) return null;

  return (
    <section className="relative py-section overflow-hidden" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Decorative parallax shapes */}
      <ParallaxLayer speed={0.3} className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-24 h-24 border-2 border-brand-400/10 rounded-lg rotate-12" />
        <div className="absolute bottom-32 left-8 w-16 h-16 bg-brand-500/5 rounded-full" />
      </ParallaxLayer>

      <Container className="relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Image with TiltCard — slide in from left */}
          <ScrollReveal animation="fade-right">
            <TiltCard className="relative">
              <div className="gradient-border rounded-2xl overflow-hidden">
                <OptimizedImage
                  src={data.image.url}
                  alt={data.image.alt}
                  className="w-full h-auto rounded-2xl"
                  wrapperClassName="rounded-2xl overflow-hidden shadow-card-hover"
                  aspectRatio="4/3"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-brand-500/20 rounded-2xl -z-10" />
              <div className="absolute -top-4 -left-4 w-16 h-16 bg-brand-400/20 rounded-2xl -z-10" />
            </TiltCard>
          </ScrollReveal>

          {/* Content — slide in from right */}
          <ScrollReveal animation="fade-left">
            <div>
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                className="inline-block px-3 py-1 mb-4 text-sm font-semibold text-brand-500 bg-brand-100 rounded-pill"
              >
                Why Calisthenics?
              </motion.span>

              <TextReveal
                text={data.title}
                as="h2"
                mode="word"
                stagger={0.06}
                className="text-3xl sm:text-4xl font-heading font-bold text-[var(--color-text-primary)] mb-6"
              />

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="text-base leading-relaxed mb-8 text-[var(--color-text-secondary)]"
              >
                {data.description}
              </motion.p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                {features.map((feat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + i * 0.1, duration: 0.5 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-8 h-8 rounded-lg bg-brand-500/10 flex items-center justify-center flex-shrink-0">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <motion.path
                          d="M3 8L7 12L13 4"
                          stroke="#5396BD"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          variants={checkVariants}
                          initial="hidden"
                          whileInView="visible"
                          viewport={{ once: true }}
                          custom={i}
                        />
                      </svg>
                    </div>
                    <span className="text-sm font-medium text-[var(--color-text-primary)]">{feat}</span>
                  </motion.div>
                ))}
              </div>

              <MagneticButton>
                <Link
                  to={data.ctaButton.link}
                  className="inline-flex items-center gap-2 px-6 py-3 text-base font-semibold text-white bg-brand-500 hover:bg-brand-600 rounded-xl transition-all duration-200 shadow-md hover:shadow-lg group"
                >
                  {data.ctaButton.text}
                  <FiArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </MagneticButton>
            </div>
          </ScrollReveal>
        </div>
      </Container>
    </section>
  );
};

export default WhyCalisthenicsSection;
